import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { dashboardCountersApi, DashboardCounters } from '../../api/dashboardCountersApi';

interface DashboardCountersContextType {
  counters: DashboardCounters | null;
  loading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
}

const DashboardCountersContext = createContext<DashboardCountersContextType | undefined>(undefined);

interface DashboardCountersProviderProps {
  siteId: number;
  children: ReactNode;
}

export const DashboardCountersProvider: React.FC<DashboardCountersProviderProps> = ({ siteId, children }) => {
  const [counters, setCounters] = useState<DashboardCounters | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const isInitialLoad = React.useRef(true);

  const loadCounters = useCallback(async (showLoading: boolean = false) => {
    try {
      if (showLoading || isInitialLoad.current) {
        setLoading(true);
      }
      setError(null);
      const data = await dashboardCountersApi.getDashboardCounters(siteId);
      setCounters(data);
      isInitialLoad.current = false;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Ошибка загрузки счетчиков');
      setError(error);
      console.error('Ошибка при загрузке счетчиков дашборда:', err);
      isInitialLoad.current = false;
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  useEffect(() => {
    isInitialLoad.current = true;
    loadCounters(true);
  }, [loadCounters]);

  const value: DashboardCountersContextType = {
    counters,
    loading,
    error,
    refresh: () => loadCounters(true),
  };

  return (
    <DashboardCountersContext.Provider value={value}>
      {children}
    </DashboardCountersContext.Provider>
  );
};

export const useDashboardCounters = (): DashboardCountersContextType | null => {
  const context = useContext(DashboardCountersContext);
  return context || null;
};
